import React, { useMemo } from 'react';
import { Input } from '../../../components/ui/Input';
import { COLORS } from '../../../theme';

// Dentro das seções coloridas desta aba, os campos de escrita ficam com fundo
// branco puro (em vez do cinza-claro padrão do Input) pra não misturar com o tom da seção.
const CAMPO_BRANCO = { background: '#fff' };

// Fonte um pouco maior só na coluna direita (Resumo/Tabelas de Preço).
const CAMPO_GRANDE = { fontSize: 14, padding: '9px 10px' };
const CAMPO_BRANCO_GRANDE = { ...CAMPO_BRANCO, ...CAMPO_GRANDE };

// Cores por seção, só nesta aba (o resto do sistema usa a cor única do Secao.js padrão).
const CORES = {
  formacao: '#6b7280',
  compra: '#6b7280',
  venda: '#dc2626',
  resumo: '#2563eb',
  tabelas: '#16a34a',
  ultimaCompra: '#0891b2'
};

function SecaoCor({ titulo, cor, children, style }) {
  return (
    <div style={{ background: `${cor}14`, borderRadius: 10, padding: '13px 16px', ...style }}>
      <div style={{ borderBottom: `2px solid ${cor}66`, paddingBottom: 6, marginBottom: 11 }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: cor, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {titulo}
        </span>
      </div>
      {children}
    </div>
  );
}

function num(v) { return Number(v) || 0; }

function calcularAoVivo(d, regimeTributario) {
  const custo_liquido = num(d.custo_original) + num(d.outros_custo) + num(d.frete_valor)
    + num(d.icms_compra_valor) + num(d.dif_icms_valor) + num(d.st_custo_valor) + num(d.ipi_compra_valor);

  const custo_operacional_total_pct = num(d.diversos_pct) + num(d.comissao_pct) + num(d.custo_fixo_pct);
  const impostos_total_pct = num(d.icms_venda_pct) + num(d.csll_pct) + num(d.pis_pct) + num(d.cofins_pct);
  const total_geral_pct = custo_operacional_total_pct + impostos_total_pct;
  const total_custos = custo_liquido * total_geral_pct / 100;

  // Lucro Real é não-cumulativo: o PIS/COFINS pago na compra (sobre o valor da
  // mercadoria, sem o ST) vira crédito que abate do PIS/COFINS devido na venda,
  // reduzindo a base de custo usada pra achar o preço — mesma lógica do backend.
  const credito_pis_cofins = regimeTributario === 'lucro_real'
    ? num(d.custo_original) * (num(d.pis_pct) + num(d.cofins_pct)) / 100
    : 0;

  // Mesma lógica pro ICMS normal (não-ST): o ICMS destacado na compra vira crédito
  // contra o ICMS devido na venda. Só vale sem ST — ST é definitivo, sem crédito.
  const temST = num(d.st_pct) > 0 || num(d.st_custo_valor) > 0;
  const credito_icms = (regimeTributario === 'lucro_real' && !temST) ? num(d.icms_compra_valor) : 0;

  const custo_base_margem = custo_liquido - credito_pis_cofins - credito_icms;

  const margemValor = (pct) => {
    const divisor = 1 - ((total_geral_pct + num(pct)) / 100);
    return divisor > 0 ? custo_base_margem / divisor : 0;
  };

  return {
    custo_liquido, custo_operacional_total_pct, impostos_total_pct, total_geral_pct, total_custos, credito_pis_cofins, credito_icms,
    margem_varejo_valor: margemValor(d.margem_varejo_pct),
    margem_geral_valor: margemValor(d.margem_geral_pct),
    margem_promocao_valor: margemValor(d.margem_promocao_pct),
    margem_licitacao_valor: margemValor(d.margem_licitacao_pct),
    margem_minimo_gerente_valor: margemValor(d.margem_minimo_gerente_pct),
    margem_minimo_valor: margemValor(d.margem_minimo_pct)
  };
}

// Campo vazio == 0 pros cálculos (num() já trata), mas exibir "0" pronto no input
// atrapalha a digitação (base some, vira "015" etc) — por isso os campos editáveis
// usam `|| ''` em vez de `?? ''`, escondendo o zero e deixando o campo em branco.
function CampoPar({ label, campoPct, campoValor, dados, onAlterarPar }) {
  return (
    <>
      <Input label={`${label} %`} type="number" step="0.01" value={dados[campoPct] || ''} style={CAMPO_BRANCO}
        onChange={e => onAlterarPar(campoPct, campoValor, 'pct', e.target.value)} />
      <Input label={`${label} R$`} type="number" step="0.01" value={dados[campoValor] || ''} style={CAMPO_BRANCO}
        onChange={e => onAlterarPar(campoPct, campoValor, 'valor', e.target.value)} />
    </>
  );
}

function formatarReais(v) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function LinhaPreco({ titulo, campoPct, valor, dados, onChange }) {
  // Lucro = Preço × Margem% — é a própria definição de margem (lucro/preço), não
  // "preço menos custo" (isso incluiria os % de imposto/comissão como se fossem lucro).
  const lucro = valor * num(dados[campoPct]) / 100;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 108px 108px', gap: 8, alignItems: 'end' }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, paddingBottom: 8 }}>{titulo}</span>
      <Input type="number" step="0.01" value={dados[campoPct] || ''} style={CAMPO_BRANCO_GRANDE} onChange={e => onChange(campoPct, e.target.value)} />
      <Input value={formatarReais(valor)} disabled style={{ ...CAMPO_GRANDE, fontWeight: 700 }} />
      <Input value={formatarReais(lucro)} disabled style={{ ...CAMPO_GRANDE, fontWeight: 700, color: '#15803d' }} />
    </div>
  );
}

export default function FormacaoPrecoTab({ dados, onChange, regimeTributario }) {
  const calculado = useMemo(() => calcularAoVivo(dados, regimeTributario), [dados, regimeTributario]);

  const onAlterarPar = (campoPct, campoValor, tipo, novoValor) => {
    const base = num(dados.custo_original);
    if (tipo === 'pct') {
      const pct = num(novoValor);
      onChange(campoPct, novoValor);
      if (base) onChange(campoValor, (base * pct / 100).toFixed(4));
    } else {
      const valorReais = num(novoValor);
      onChange(campoValor, novoValor);
      if (base) onChange(campoPct, (valorReais / base * 100).toFixed(4));
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '660px 1fr', gap: 20, alignItems: 'stretch' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <SecaoCor titulo="Formação de Preço" cor={CORES.formacao}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
            <Input label="Custo Original R$" type="number" step="0.01" value={dados.custo_original || ''} style={CAMPO_BRANCO} onChange={e => onChange('custo_original', e.target.value)} />
            <Input label="Outros R$" type="number" step="0.01" value={dados.outros_custo || ''} style={CAMPO_BRANCO} onChange={e => onChange('outros_custo', e.target.value)} />
            <CampoPar label="Frete" campoPct="frete_pct" campoValor="frete_valor" dados={dados} onAlterarPar={onAlterarPar} />
          </div>
        </SecaoCor>

        <SecaoCor titulo="Custo de Compra" cor={CORES.compra}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
              <CampoPar label="ICMS" campoPct="icms_compra_pct" campoValor="icms_compra_valor" dados={dados} onAlterarPar={onAlterarPar} />
              <CampoPar label="Dif. ICMS" campoPct="dif_icms_pct" campoValor="dif_icms_valor" dados={dados} onAlterarPar={onAlterarPar} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
              <CampoPar label="ST" campoPct="st_pct" campoValor="st_custo_valor" dados={dados} onAlterarPar={onAlterarPar} />
              <CampoPar label="IPI" campoPct="ipi_compra_pct" campoValor="ipi_compra_valor" dados={dados} onAlterarPar={onAlterarPar} />
            </div>
          </div>
        </SecaoCor>

        <SecaoCor titulo="Custo de Venda" cor={CORES.venda}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
            <Input label="ICMS %" type="number" step="0.01" value={dados.icms_venda_pct || ''} style={CAMPO_BRANCO} onChange={e => onChange('icms_venda_pct', e.target.value)} />
            <Input label="CSLL %" type="number" step="0.01" value={dados.csll_pct || ''} style={CAMPO_BRANCO} onChange={e => onChange('csll_pct', e.target.value)} />
            <Input label="PIS %" type="number" step="0.01" value={dados.pis_pct || ''} style={CAMPO_BRANCO} onChange={e => onChange('pis_pct', e.target.value)} />
            <Input label="COFINS %" type="number" step="0.01" value={dados.cofins_pct || ''} style={CAMPO_BRANCO} onChange={e => onChange('cofins_pct', e.target.value)} />
            <Input label="Diversos %" type="number" step="0.01" value={dados.diversos_pct || ''} style={CAMPO_BRANCO} onChange={e => onChange('diversos_pct', e.target.value)} />
            <Input label="Comissão %" type="number" step="0.01" value={dados.comissao_pct || ''} style={CAMPO_BRANCO} onChange={e => onChange('comissao_pct', e.target.value)} />
            <Input label="Custo Fixo %" type="number" step="0.01" value={dados.custo_fixo_pct || ''} style={CAMPO_BRANCO} onChange={e => onChange('custo_fixo_pct', e.target.value)} />
          </div>
        </SecaoCor>

        <SecaoCor titulo="Última Compra / Estoque Atual" cor={CORES.ultimaCompra}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
            <Input label="Preço Última Compra" value={dados.preco_ultima_compra != null ? formatarReais(Number(dados.preco_ultima_compra)) : '-'} disabled />
            <Input label="Data Última Compra" value={dados.data_ultima_compra ?? '-'} disabled />
            <Input label="Estoque Atual" value={dados.estoque_atual_cache ?? '0'} disabled />
            <Input label="Custo de Produção" type="number" step="0.01" value={dados.custo_producao || ''} style={CAMPO_BRANCO} onChange={e => onChange('custo_producao', e.target.value)} />
          </div>
        </SecaoCor>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 640 }}>
        <SecaoCor titulo="Resumo" cor={CORES.resumo}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Input label="Custo Total do Produto" value={formatarReais(calculado.custo_liquido)} disabled style={{ fontWeight: 700, ...CAMPO_GRANDE }} />
            {regimeTributario === 'lucro_real' && (calculado.credito_pis_cofins > 0 || calculado.credito_icms > 0) && (
              <div style={{ display: 'grid', gridTemplateColumns: calculado.credito_icms > 0 ? '1fr 1fr' : '1fr', gap: 8 }}>
                <Input label="Crédito PIS/COFINS (compra)" value={formatarReais(calculado.credito_pis_cofins)} disabled style={{ ...CAMPO_GRANDE, color: '#15803d' }} />
                {calculado.credito_icms > 0 && (
                  <Input label="Crédito ICMS (compra)" value={formatarReais(calculado.credito_icms)} disabled style={{ ...CAMPO_GRANDE, color: '#15803d' }} />
                )}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <Input label="% Custo Operacional" value={calculado.custo_operacional_total_pct.toFixed(2)} disabled style={CAMPO_GRANDE} />
              <Input label="% Impostos" value={calculado.impostos_total_pct.toFixed(2)} disabled style={CAMPO_GRANDE} />
            </div>
            <Input label="Total Geral %" value={calculado.total_geral_pct.toFixed(2)} disabled style={CAMPO_GRANDE} />
          </div>
        </SecaoCor>

        <SecaoCor titulo="Tabelas de Preço" cor={CORES.tabelas} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 108px 108px', gap: 8, fontSize: 11, fontWeight: 700, color: COLORS.textLight, textTransform: 'uppercase', paddingBottom: 2 }}>
              <span>Tabela</span><span>%</span><span>Preço R$</span><span>Lucro R$</span>
            </div>
            <LinhaPreco titulo="Varejo" campoPct="margem_varejo_pct" valor={calculado.margem_varejo_valor} dados={dados} onChange={onChange} />
            <LinhaPreco titulo="Geral" campoPct="margem_geral_pct" valor={calculado.margem_geral_valor} dados={dados} onChange={onChange} />
            <LinhaPreco titulo="Promoção" campoPct="margem_promocao_pct" valor={calculado.margem_promocao_valor} dados={dados} onChange={onChange} />
            <LinhaPreco titulo="Licitação" campoPct="margem_licitacao_pct" valor={calculado.margem_licitacao_valor} dados={dados} onChange={onChange} />
            <LinhaPreco titulo="Mínimo Gerente" campoPct="margem_minimo_gerente_pct" valor={calculado.margem_minimo_gerente_valor} dados={dados} onChange={onChange} />
            <LinhaPreco titulo="Preço Mínimo" campoPct="margem_minimo_pct" valor={calculado.margem_minimo_valor} dados={dados} onChange={onChange} />
          </div>
        </SecaoCor>
      </div>
    </div>
  );
}
